import { Loader } from 'components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { FacebookLogo } from 'static';
import { getAuthFlowFromPath, logInFacebook, SIGNUP_PATH } from 'utils/auth';

import { useResetOAuthButtonLoadingOnReturn } from '../useResetOAuthButtonLoadingOnReturn';
import { Button } from './styled';

interface FacebookAuthButtonProps {
  disabled?: boolean;
  forceLoading?: boolean;
  onAuthStart?: () => void;
  highlighted?: boolean;
  style?: { [key: string]: string };
  iconOnly?: boolean;
}

const FacebookAuthButton = ({
  style,
  disabled,
  forceLoading,
  onAuthStart,
  highlighted,
  iconOnly,
}: FacebookAuthButtonProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);
  const [prevAuth] = useLocalStorage<{ [key: string]: string }>(
    'socialSignIns',
    {},
  );
  const buttonText = useMemo(() => {
    let text = `${t('auth.signInWith')} Facebook`;

    if (location.pathname !== SIGNUP_PATH && prevAuth?.facebook) {
      text = `${t('auth.continueAs')} ${prevAuth.facebook}`;
    }

    return text;
  }, [prevAuth, location, t]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useResetOAuthButtonLoadingOnReturn(setIsLoading, isMountedRef);

  const handleClick = () => {
    if (isLoading || forceLoading) return;
    onAuthStart?.();
    setIsLoading(true);
    logInFacebook({
      flow: getAuthFlowFromPath(location.pathname),
    }).catch(() => {
      if (!isMountedRef.current) return;
      setIsLoading(false);
    });
  };

  const effectiveLoading = Boolean(forceLoading || isLoading);

  return (
    <Button
      style={style}
      type="button"
      disabled={disabled || effectiveLoading}
      onClick={handleClick}
      $highlighted={highlighted && !disabled && !effectiveLoading}
      $iconOnly={iconOnly}
    >
      {effectiveLoading ? (
        <Loader type="button" size="20px" thickness={2} />
      ) : (
        <>
          <FacebookLogo />
          {!iconOnly && buttonText}
        </>
      )}
    </Button>
  );
};

export default FacebookAuthButton;
